import { useState, useCallback, useMemo } from 'react';

import {
  useGetTradingAccounts,
  TradingAccountsFetchFilter,
} from '../../api/accounts';
import { useAppContext } from '../../contexts/Storage';
import { useCurrency } from '../../utils/globalCurrency';
import { AccountListItem } from '../../models/AccountListItem';

interface UseTradingAccountsOptions {
  recordId?: number;
}

// Types for list item format used by ListPageTemplate
export interface ListItem {
  id: number | string;
  name: string;
  description?: string;
  category?: string;
  type?: string;
  balance?: number | string;
}

const useTradingAccounts = (options: UseTradingAccountsOptions = {}) => {
  const { recordId: initialRecordId } = options;
  const { user, selectedRecord } = useAppContext();

  // Use provided recordId or fall back to selected record
  const recordId = initialRecordId ?? selectedRecord?.id;
  const userId = user?.id;

  // Filter state
  const [filters, setFilters] = useState<Partial<TradingAccountsFetchFilter>>({
    per_page: 15,
    page: 1,
  });

  // Search state (client-side filtering)
  const [searchTerm, setSearchTerm] = useState('');

  // API hook - only fetch when we have required params
  const hasRequiredParams = !!recordId && !!userId;
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useGetTradingAccounts({
    filters: {
      ...filters,
      record_id: recordId,
      user_id: userId,
    },
    enabled: hasRequiredParams,
  });

  // Raw accounts from API
  const accounts = useMemo(() => data?.accounts ?? [], [data?.accounts]);
  const pagination = useMemo(() => data?.pagination, [data?.pagination]);

  // Filter by search term (client-side)
  const filteredAccounts = useMemo(() => {
    if (!searchTerm.trim()) {
      return accounts;
    }
    const term = searchTerm.toLowerCase();
    return accounts.filter((account: AccountListItem) =>
      account.transaction_entity_name?.toLowerCase().includes(term) ||
      account.transaction_entity_description?.toLowerCase().includes(term)
    );
  }, [accounts, searchTerm]);

  // Get currency formatting
  const { formatWhole } = useCurrency();

  // Map to ListPageTemplate format
  const listItems = useMemo((): ListItem[] => {
    return filteredAccounts
      .filter((account: AccountListItem) => account.id !== undefined)
      .map((account: AccountListItem) => ({
        id: account.id as number,
        name: account.transaction_entity_name || 'Unknown',
        description: account.transaction_entity_description || '',
        category: account.transaction_entity_category,
        type: account.transaction_entity_type,
        balance: account.transaction_entity_balance_amount !== undefined
          ? formatWhole(account.transaction_entity_balance_amount)
          : undefined,
      }));
  }, [filteredAccounts, formatWhole]);

  // Filter handlers
  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const handlePerPageChange = useCallback((perPage: number) => {
    setFilters((prev) => ({ ...prev, per_page: perPage, page: 1 }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      per_page: 15,
      page: 1,
    });
    setSearchTerm('');
  }, []);

  return {
    // Data
    accounts,
    filteredAccounts,
    listItems,
    pagination,

    // Loading states
    isLoading,
    hasRequiredParams,

    // Error state
    error,

    // Search and filter state
    searchTerm,
    filters,

    // Handlers
    handleSearchChange,
    handlePageChange,
    handlePerPageChange,
    resetFilters,

    // Refetch
    refetch,
  };
};

export default useTradingAccounts;
